import { useRef, useCallback, useEffect } from 'react';

/**
 * useRoomPolling manages regular and burst polling intervals for a game room.
 * - manualPoll: function to trigger a one-off poll
 * - startBurstPolling: function to start burst polling (rapid polling for a short period)
 *
 * @param pollFn - async function to call for polling
 * @param options - { burstCount, burstInterval, regularInterval }
 */
export function useRoomPolling(pollFn: () => Promise<void>, options?: {
  burstCount?: number;
  burstInterval?: number;
  regularInterval?: number;
}) {
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  const burstPollInterval = useRef<NodeJS.Timeout | null>(null);
  const burstPollCount = useRef(0);
  const burstCount = options?.burstCount ?? 5;
  const burstInterval = options?.burstInterval ?? 700;
  const regularInterval = options?.regularInterval ?? 3000;

  // Manual polling
  const manualPoll = useCallback(() => {
    pollFn();
  }, [pollFn]);

  // Burst polling (rapid polling for a short period)
  const startBurstPolling = useCallback(() => {
    if (burstPollInterval.current) return; // Already running
    burstPollCount.current = 0;
    burstPollInterval.current = setInterval(async () => {
      burstPollCount.current += 1;
      await pollFn();
      if (burstPollCount.current >= burstCount) {
        clearInterval(burstPollInterval.current!);
        burstPollInterval.current = null;
        burstPollCount.current = 0;
      }
    }, burstInterval);
  }, [pollFn, burstCount, burstInterval]);

  // Regular polling
  useEffect(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }
    pollInterval.current = setInterval(pollFn, regularInterval);
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
      }
      if (burstPollInterval.current) {
        clearInterval(burstPollInterval.current);
        burstPollInterval.current = null;
      }
    };
  }, [pollFn, regularInterval]);

  return {
    manualPoll,
    startBurstPolling,
  };
}
